import React from "react";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: "📄",
      title: "Information We Collect",
      content: "We collect personal details such as your name, email, phone number, and location when you register or use our services. Additional information may be collected through support chats, property management activities, and payment processing."
    },
    {
      icon: "👥",
      title: "How We Use Your Information",
      content: "Your data is used to provide and improve NLB services, process payments, personalize your experience, and communicate updates or support messages."
    },
    {
      icon: "🔒",
      title: "Data Protection",
      content: "We use secure technologies and encryption to protect your data from unauthorized access. Access to your data is restricted to authorized personnel only."
    },
    {
      icon: "🛡️",
      title: "Sharing of Information",
      content: "We do not sell or rent your information. We may share data with trusted third parties (e.g., payment processors) strictly to deliver our services."
    },
    {
      icon: "🍪",
      title: "Cookies and Tracking",
      content: "NLB uses cookies to enhance user experience, track analytics, and improve performance. You may disable cookies through your browser settings."
    },
    {
      icon: "⚖️",
      title: "Your Rights",
      content: "You can access, update, or request deletion of your personal data by contacting us at support@nrilocalbuddy.com."
    }
  ];

  return (
    <section className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-xl"
          >
            <span className="text-4xl">🛡️</span>
          </motion.div>
          
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            At <strong className="text-indigo-600">NRI Local Buddy (NLB)</strong>, we value your privacy and
            are committed to protecting your personal information. This policy
            explains how we collect, use, and safeguard your data.
          </p>
        </motion.div>

        {/* Policy Sections */}
        <div className="grid gap-6 mb-12">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center text-2xl">
                  {section.icon}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                    {index + 1}. {section.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Updates Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: sections.length * 0.1, duration: 0.5 }}
            className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-8 shadow-lg text-white"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm text-2xl">
                🔔
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-semibold mb-3">
                  7. Updates to This Policy
                </h2>
                <p className="leading-relaxed opacity-95">
                  This Privacy Policy may be updated periodically. Any major changes
                  will be communicated via email or app notifications.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Contact Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center"
        >
          <div className="text-5xl mb-4">✉️</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Questions About Your Privacy?
          </h3>
          <p className="text-gray-600 mb-4">
            We're here to help. Reach out to our support team anytime.
          </p>
          <a
            href="mailto:hello@ay-technologies.com"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all duration-300 hover:scale-105"
          >
            📧 hello@ay-technologies.com
          </a>
        </motion.div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            Last updated: October 2025
          </p>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;